import Head from "next/head";
import { createContext, ReactNode, useContext, useState } from "react";

import styled from "styled-components";

const PageContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
`;

const PageHeaderContainer = styled.div`
  flex: 0;
  border-bottom: 1px solid var(--aa-border-subtle);
  position: relative;
  /* Stack above positioned content in Page.Body — e.g. the sticky message
     table headers (z-index 2) — so menus opening out of the header (like
     react-select menus at z-index 1) aren't painted over by it. */
  z-index: 3;
  padding: 20px 30px;
`;

const PageBody = styled.div`
  flex: 1;
  flex-basis: 100%;
  min-height: 0;
  overflow: auto;
  /* Exposed as a variable so sticky descendants can offset themselves to the
     visible top of the scrollport (sticky "top" is relative to the content
     edge, i.e. below this padding). */
  --aa-page-body-padding-top: 20px;
  padding: var(--aa-page-body-padding-top) 30px 20px;
  scrollbar-gutter: stable;
`;

const StyledButton = styled.a`
  margin: 20px;
  position: absolute;
  bottom: 0;
  right: 0;
`;

export const SiteTitleContext = createContext<string | null>(null);

const Page = ({
  children,
  title = null,
}: {
  children?: ReactNode;
  title?: string | null;
}) => {
  const siteTitle = useContext(SiteTitleContext);

  let fullTitle;
  if (siteTitle) fullTitle = title ? `${siteTitle}: ${title}` : siteTitle;
  else fullTitle = null;

  return (
    <PageContainer>
      {fullTitle && (
        <Head>
          <title>{fullTitle}</title>
        </Head>
      )}
      {children}
    </PageContainer>
  );
};

const PageHeaderToggle = ({
  onToggle,
  collapsed,
}: {
  onToggle: () => void;
  collapsed?: boolean;
}) => {
  return (
    <StyledButton onClick={onToggle}>
      {collapsed ? <>Show more</> : <>Show less</>}
    </StyledButton>
  );
};

const PageHeader = ({
  children,
  collapsible,
  defaultCollapsed,
  className,
}: {
  children?: ReactNode | (({ collapsed }: { collapsed: boolean }) => ReactNode);
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  className?: string;
}) => {
  const [collapsed, setCollapsed] = useState(
    Boolean(defaultCollapsed !== undefined && defaultCollapsed)
  );

  return (
    <PageHeaderContainer className={className}>
      {typeof children === "function" ? children({ collapsed }) : children}
      {collapsible && (
        <PageHeaderToggle
          collapsed={collapsed}
          onToggle={() => {
            setCollapsed(!collapsed);
          }}
        />
      )}
    </PageHeaderContainer>
  );
};

export default Object.assign(Page, {
  Header: PageHeader,
  Body: PageBody,
});
